import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Combobox,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Option,
    Textarea,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Checkmark16Regular, Delete24Regular, Dismiss24Regular, Edit24Regular, Open24Regular, Save24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RequiredFieldFrame from '../../components/RequiredFieldFrame';
import { useProject } from '../../context/ProjectContext';
import type { ExternalLinkItem } from '../../models/ExternalLinkItem';
import type { IDataItem } from '../../models/IDataItem';
import type { ProjectUserInfo } from '../../models/ProjectUserInfo';
import type { ProjektArtItem } from '../../models/ProjektArtItem';
import type { ProjektInstitutItem } from '../../models/ProjektInstitutItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektmitarbeiterItem } from '../../models/ProjektmitarbeiterItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';
import { getVisibleMissingEckdatenFields, isRequiredFieldMissing } from '../../utils/eckdatenCompleteness';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
    },
    footerBar: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: tokens.spacingVerticalXXL,
        paddingTop: tokens.spacingVerticalL,
        borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
        gap: tokens.spacingHorizontalM,
    },
    externalLinkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    externalLinkButton: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        cursor: 'pointer',
        color: tokens.colorNeutralBackground1,
        textDecoration: 'none',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        border: `1px solid ${tokens.colorBrandStroke1}`,
        fontSize: tokens.fontSizeBase400,
    },
    externalLinksList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    externalLinksListItem: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        padding: `${tokens.spacingVerticalS} 0`,
        borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    externalLinksListItemLink: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        color: tokens.colorBrandForeground1,
        textDecoration: 'none',
        cursor: 'pointer',
        flex: 1,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    categorySection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    categoryHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    fieldBox: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalM,
        alignItems: 'start',
        paddingBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingTop: '4px',
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        wordWrap: 'break-word',
    },
    emptyValue: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    booleanValue: {
        padding: '2px 6px',
        borderRadius: '2px',
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-block',
        width: 'fit-content',
    },
    booleanTrue: {
        backgroundColor: tokens.colorStatusSuccessBackground2,
        color: tokens.colorStatusSuccessForeground1,
    },
    booleanFalse: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    inputField: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        height: '32px',
    },
    largeTextarea: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        minHeight: '120px',
        resize: 'none',
    },
    tableContainer: {
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '4px',
        overflow: 'hidden',
        flex: 1,
        minWidth: '200px',
    },
    tableRow: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalM,
        flexWrap: 'wrap',
    },
    table: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
    },
    tableHeaderRow: {
        backgroundColor: tokens.colorNeutralBackground3,
    },
});

interface EckdatenAllgemeinProps {
    project: ProjektItem | null;
}

interface FieldConfig {
    key: string;
    label: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: any;
    isBoolean?: boolean;
    isTextarea?: boolean;
    isLargeTextarea?: boolean;
    isDate?: boolean;
    isSelect?: boolean;
    selectType?: 'status' | 'institute' | 'projektart' | 'mitarbeiter';
    editable?: boolean;
    // Permission configuration: specify which roles/conditions can edit this field
    permissions?: {
        isBenutzerProjektLeiter?: 'r' | 'rw';
        isBenutzerProjektleiterAssistent?: 'r' | 'rw';
        isRegularUser?: 'r' | 'rw';
        'Rolle Administratoren'?: 'r' | 'rw';
        'Rolle Hochschulleitung'?: 'r' | 'rw';
        'Rolle Controlling'?: 'r' | 'rw';
        'Rolle Accounting'?: 'r' | 'rw';
        'Rolle Vertragskoordination'?: 'r' | 'rw';
        'Rolle Forschungsservice'?: 'r' | 'rw';
        'Rolle Rektorat'?: 'r' | 'rw';
        departmentHead?: 'r' | 'rw';
    };
}

interface EditedValues {
    [key: string]: string | boolean | number | undefined | ExternalLinkItem[];
}

/**
 * Determine if the current user can edit a field based on their role and permissions
 */
const canEditField = (
    fieldPermissions: FieldConfig['permissions'] | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager
    if (project.isBenutzerProjektLeiter && fieldPermissions.isBenutzerProjektLeiter === 'rw') {
        return true;
    }

    // Check if user is a project manager assistant
    if (project.isBenutzerProjektleiterAssistent && fieldPermissions.isBenutzerProjektleiterAssistent === 'rw') {
        return true;
    }

    // Check if user is department head (if departmentLeiterID matches current user)
    if (userInfo && project.departmentLeiterID === userInfo.userId && fieldPermissions.departmentHead === 'rw') {
        return true;
    }

    // Check role-based permissions
    if (userRole === 'Administratoren' && fieldPermissions['Rolle Administratoren'] === 'rw') {
        return true;
    }

    if (userRole === 'Hochschulleitung' && fieldPermissions['Rolle Hochschulleitung'] === 'rw') {
        return true;
    }

    if (userRole === 'Controlling' && fieldPermissions['Rolle Controlling'] === 'rw') {
        return true;
    }

    if (userRole === 'Accounting' && fieldPermissions['Rolle Accounting'] === 'rw') {
        return true;
    }

    if (userRole === 'Vertragskoordination' && fieldPermissions['Rolle Vertragskoordination'] === 'rw') {
        return true;
    }

    if (userRole === 'Forschungsservice' && fieldPermissions['Rolle Forschungsservice'] === 'rw') {
        return true;
    }

    if (userRole === 'Rektorat' && fieldPermissions['Rolle Rektorat'] === 'rw') {
        return true;
    }

    // Check if user is regular user/viewer (not project manager/assistant)
    if (!project.isBenutzerProjektLeiter &&
        !project.isBenutzerProjektleiterAssistent &&
        !project.isBenutzerProjektAssistent &&
        fieldPermissions.isRegularUser === 'rw') {
        return true;
    }

    return false;
};

/**
 * Determine if the current user can view a field (read permission)
 */
const canViewField = (
    fieldPermissions: FieldConfig['permissions'] | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager (read or write)
    if (project.isBenutzerProjektLeiter &&
        (fieldPermissions.isBenutzerProjektLeiter === 'rw' || fieldPermissions.isBenutzerProjektLeiter === 'r')) {
        return true;
    }

    // Check if user is a project manager assistant (read or write)
    if (project.isBenutzerProjektleiterAssistent &&
        (fieldPermissions.isBenutzerProjektleiterAssistent === 'rw' || fieldPermissions.isBenutzerProjektleiterAssistent === 'r')) {
        return true;
    }

    // Check if user is department head (if departmentLeiterID matches current user)
    if (userInfo && project.departmentLeiterID === userInfo.userId &&
        (fieldPermissions.departmentHead === 'rw' || fieldPermissions.departmentHead === 'r')) {
        return true;
    }

    // Check role-based permissions (read or write)
    const rolePermission = userRole ?
        (fieldPermissions[`Rolle ${userRole}` as keyof typeof fieldPermissions] ||
            fieldPermissions[userRole as keyof typeof fieldPermissions]) :
        undefined;

    if (rolePermission && (rolePermission === 'rw' || rolePermission === 'r')) {
        return true;
    }

    // Check if user is regular user/viewer
    if (!project.isBenutzerProjektLeiter &&
        !project.isBenutzerProjektleiterAssistent &&
        !project.isBenutzerProjektAssistent &&
        (fieldPermissions.isRegularUser === 'rw' || fieldPermissions.isRegularUser === 'r')) {
        return true;
    }

    return false;
};

const formatDateForInput = (date: Date | string | undefined): string => {
    if (!date) return '';
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        const year = dateObj.getFullYear();

        // Treat default/placeholder dates (before year 2000) as empty
        if (year < 2000) return '';

        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const initializeEditedValues = (project: ProjektItem | null): EditedValues => {
    if (!project) return {};
    return {
        title: project.title || '',
        projekttitelLangform: project.projekttitelLangform || '',
        projektKurzbeschreibung: project.projektKurzbeschreibung || '',
        projektNutzen: project.projektNutzen || '',
        projektSonstigeAnmerkungen: project.projektSonstigeAnmerkungen || '',
        anmerkungenProjektverlauf: project.anmerkungenProjektverlauf || '',
        projektWebsiteUrl: project.projektWebsiteUrl || '',
        projektStatusAsText: String(project.projektStatus || ''),
        operativerBeginnPlan: formatDateForInput(project.operativerBeginnPlan),
        operativerBeginnIst: formatDateForInput(project.operativerBeginnIst),
        operativesEndePlan: formatDateForInput(project.operativesEndePlan),
        operativesEndeIst: formatDateForInput(project.operativesEndeIst),
        kaufmaennischesEnde: formatDateForInput(project.kaufmaennischesEnde),
        datumEinreichungAngebot: formatDateForInput(project.datumEinreichungAngebot),
        datumAbsageAngebot: formatDateForInput(project.datumAbsageAngebot),
        datumZuschlagAngebot: formatDateForInput(project.datumZuschlagAngebot),
        created: formatDateForInput(project.created),
        externalLinks: project.externalLinks || [],
    };
};

const EckdatenAllgemein: React.FC<EckdatenAllgemeinProps> = ({ project }) => {
    const styles = useStyles();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo: configUserInfo } = useConfigStore((state) => state.userConfig);
    const { accounts, instance } = useMsal();
    const { refreshProject, userInfo } = useProject();
    const [editedValues, setEditedValues] = useState<EditedValues>(() => initializeEditedValues(project));
    const [isEditing, setIsEditing] = useState(false);
    const [editedExternalLinks, setEditedExternalLinks] = useState<ExternalLinkItem[]>(project?.externalLinks || []);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Select options state
    const [statusOptions, setStatusOptions] = useState<IDataItem[]>([]);
    const [instituteOptions, setInstituteOptions] = useState<ProjektInstitutItem[]>([]);
    const [projektArtOptions, setProjektArtOptions] = useState<ProjektArtItem[]>([]);
    const [mitarbeiterOptions, setMitarbeiterOptions] = useState<ProjektmitarbeiterItem[]>([]);
    const [memberSearchResults, setMemberSearchResults] = useState<ProjektmitarbeiterItem[]>([]);
    const [memberSearchTimeout, setMemberSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const missingFields = useMemo(() => getVisibleMissingEckdatenFields(project, userInfo), [project, userInfo]);

    // All field permissions configuration
    const allFieldPermissions = useMemo(() => {
        const permissions: Array<{ key: string; permissions: FieldConfig['permissions'] }> = [
            { key: 'title', permissions: { 'Rolle Administratoren': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'projekttitelLangform', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'projektKurzbeschreibung', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'projektNutzen', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'projektSonstigeAnmerkungen', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'anmerkungenProjektverlauf', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'projektWebsiteUrl', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'projektStatusAsText', permissions: { 'Rolle Administratoren': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'operativerBeginnPlan', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'operativerBeginnIst', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'operativesEndePlan', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
            { key: 'operativesEndeIst', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'kaufmaennischesEnde', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r' } },
            { key: 'datumEinreichungAngebot', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'datumAbsageAngebot', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'datumZuschlagAngebot', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'externalLinks', permissions: { 'Rolle Administratoren': 'rw', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r' } },
        ];
        return permissions;
    }, []);

    // Determine if current user can edit any fields
    const canEditAny = useMemo(() => {
        if (!project || !configUserInfo) return false;

        // Check if user can edit at least one field
        return allFieldPermissions.some((field) => {
            return canEditField(field.permissions, selectedRole, project, configUserInfo);
        });
    }, [project, selectedRole, configUserInfo, allFieldPermissions]);

    // DataService instance
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch options when entering edit mode
    const fetchSelectOptions = useCallback(async () => {
        if (!dataService || optionsLoaded) return;

        try {
            const [statuses, institutes, arts, mitarbeiter] = await Promise.all([
                dataService.getProjektStatusItems(),
                dataService.getProjektInstitute(),
                dataService.getProjektArten(project?.departmentID || 0),
                dataService.getMitarbeiter(),
            ]);
            setStatusOptions(statuses);
            setInstituteOptions(institutes);
            setProjektArtOptions(arts);
            setMitarbeiterOptions(mitarbeiter);
            setOptionsLoaded(true);
        } catch (error) {
            console.error('Error fetching select options:', error);
        }
    }, [dataService, optionsLoaded, project?.departmentID]);

    useEffect(() => {
        if (isEditing && !optionsLoaded && dataService) {
            fetchSelectOptions();
            // Initialize member search results with all mitarbeiter options
            setMemberSearchResults(mitarbeiterOptions);
        }
    }, [isEditing, optionsLoaded, dataService, fetchSelectOptions, mitarbeiterOptions]);

    useEffect(() => {
        setEditedValues(initializeEditedValues(project));
    }, [project]);

    // Handle member search for Project Manager
    const handleMemberSearch = useCallback((searchTerm: string) => {
        if (memberSearchTimeout) clearTimeout(memberSearchTimeout);

        if (searchTerm.length < 2) {
            setMemberSearchResults([]);
            return;
        }

        const timeout = setTimeout(() => {
            const filtered = mitarbeiterOptions.filter((member) => {
                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                return fullName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setMemberSearchResults(filtered);
        }, 300);

        setMemberSearchTimeout(timeout);
    }, [mitarbeiterOptions, memberSearchTimeout]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleFieldChange = (key: string, value: any) => {
        setEditedValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!project || !dataService) return;

        setIsSaving(true);
        try {
            // Helper to convert date string (YYYY-MM-DD) back to Date in UTC to avoid timezone offset issues
            const stringToDate = (dateStr: string | undefined): Date | null | undefined => {
                if (!dateStr) return undefined;
                try {
                    const [year, month, day] = dateStr.split('-');
                    if (!year || !month || !day) return undefined;
                    // Use UTC to avoid timezone offset issues when serializing to JSON
                    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
                } catch {
                    return undefined;
                }
            };

            // Merge edited values into the project
            const updatedProject: ProjektItem = {
                ...project,
                title: editedValues.title as string,
                projekttitelLangform: editedValues.projekttitelLangform as string,
                projektKurzbeschreibung: editedValues.projektKurzbeschreibung as string,
                projektNutzen: editedValues.projektNutzen as string,
                projektSonstigeAnmerkungen: editedValues.projektSonstigeAnmerkungen as string,
                anmerkungenProjektverlauf: editedValues.anmerkungenProjektverlauf as string,
                projektWebsiteUrl: editedValues.projektWebsiteUrl as string,
                operativerBeginnPlan: stringToDate(editedValues.operativerBeginnPlan as string) ?? project.operativerBeginnPlan,
                operativerBeginnIst: stringToDate(editedValues.operativerBeginnIst as string) ?? project.operativerBeginnIst,
                operativesEndePlan: stringToDate(editedValues.operativesEndePlan as string) ?? project.operativesEndePlan,
                operativesEndeIst: stringToDate(editedValues.operativesEndeIst as string) ?? project.operativesEndeIst,
                kaufmaennischesEnde: stringToDate(editedValues.kaufmaennischesEnde as string) ?? project.kaufmaennischesEnde,
                datumEinreichungAngebot: stringToDate(editedValues.datumEinreichungAngebot as string) ?? project.datumEinreichungAngebot,
                datumAbsageAngebot: stringToDate(editedValues.datumAbsageAngebot as string) ?? project.datumAbsageAngebot,
                datumZuschlagAngebot: stringToDate(editedValues.datumZuschlagAngebot as string) ?? project.datumZuschlagAngebot,
                externalLinks: editedExternalLinks,
            };

            // Call API to save
            const result = await dataService.updateProjekt(updatedProject);

            if (result.success) {
                if (refreshProject) {
                    await refreshProject();
                }
                setShowSuccessMessage(true);
                setErrorMessage(null);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                setIsEditing(false);
            } else {
                // Show error message
                setShowSuccessMessage(false);
                setErrorMessage(result.message);
                setTimeout(() => {
                    setErrorMessage(null);
                }, 5000);
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error saving changes. Please try again.');
            setTimeout(() => {
                setErrorMessage(null);
            }, 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        setEditedValues(initializeEditedValues(project));
        setEditedExternalLinks(project?.externalLinks || []);
        setIsEditing(false);
        setMemberSearchResults([]);
        if (memberSearchTimeout) clearTimeout(memberSearchTimeout);
    };

    const handleAddExternalLink = () => {
        setEditedExternalLinks([...editedExternalLinks, { title: '', url: '', target: '_blank' }]);
    };

    const handleRemoveExternalLink = (index: number) => {
        setEditedExternalLinks(editedExternalLinks.filter((_, i) => i !== index));
    };

    const handleExternalLinkChange = (index: number, field: 'title' | 'url', value: string) => {
        const updated = [...editedExternalLinks];
        updated[index] = { ...updated[index], [field]: value };
        setEditedExternalLinks(updated);
    };

    const renderDisplayField = (config: FieldConfig) => {
        let displayValue = config.value;
        let valueContent: React.ReactNode = null;

        // For date fields in display mode, use the AsText version if available from project
        if (config.isDate && project) {
            const textFieldKey = config.key + 'AsText';
            displayValue = (project as unknown as Record<string, unknown>)[textFieldKey] || config.value;
        }

        // For select fields (status, department, projektart), use the text values from project
        if (config.isSelect && project) {
            switch (config.selectType) {
                case 'status':
                    displayValue = project.projektStatusAsText || config.value;
                    break;
                case 'institute':
                    displayValue = project.department || config.value;
                    break;
                case 'projektart':
                    displayValue = project.projektart || config.value;
                    break;
            }
        }

        if (displayValue === null || displayValue === undefined || displayValue === '') {
            valueContent = <div className={`${styles.fieldValue} ${styles.emptyValue}`}>-</div>;
        } else if (config.isBoolean) {
            const isTrue = displayValue === true || displayValue === 'Ja' || displayValue === 'Yes' || displayValue === 'true' || displayValue === 1;
            valueContent = (
                <div className={`${styles.booleanValue} ${isTrue ? styles.booleanTrue : styles.booleanFalse}`}>
                    {isTrue ? 'Yes' : 'No'}
                </div>
            );
        } else {
            valueContent = <div className={styles.fieldValue}>{displayValue}</div>;
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{config.label}</div>
                {valueContent}
            </div>
        );
    };

    const renderEditField = (config: FieldConfig) => {
        const value = String(editedValues[config.key] || '');

        if (config.isBoolean) {
            const isTrue = value === 'Ja' || value === 'Yes' || value === 'true' || value === '1' || editedValues[config.key] === true;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <div style={{ marginTop: '2px' }}>
                        <Checkbox
                            checked={isTrue}
                            onChange={(_ev, data) => {
                                handleFieldChange(config.key, data.checked ? 1 : 0);
                            }}
                        />
                    </div>
                </div>
            );
        }

        if (config.isDate) {
            const dateValue = value ? new Date(value) : undefined;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <DatePicker
                        value={dateValue}
                        onSelectDate={(date) => {
                            if (date) {
                                const formatted = formatDateForInput(date);
                                handleFieldChange(config.key, formatted);
                            } else {
                                handleFieldChange(config.key, '');
                            }
                        }}
                        placeholder="Select date"
                        formatDate={(date) => {
                            if (!date) return '';
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}.${month}.${year}`;
                        }}
                        parseDateFromString={(dateString) => {
                            const parts = dateString.split('.');
                            if (parts.length === 3) {
                                const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                                return date;
                            }
                            return null;
                        }}
                    />
                </div>
            );
        }

        if (config.isSelect && config.selectType) {
            // For Project Manager, use searchable Combobox
            if (config.selectType === 'mitarbeiter') {
                return (
                    <div className={styles.fieldBox}>
                        <div className={styles.fieldLabel}>{config.label}</div>
                        <Combobox
                            placeholder="Enter at least 2 characters..."
                            value={value}
                            onOptionSelect={(_, option) => {
                                const member = memberSearchResults.find((m) => m.personalID === parseInt(option.optionValue || '0'));
                                if (member) {
                                    const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                    handleFieldChange(config.key, fullName);
                                }
                            }}
                            onChange={(ev) => {
                                const searchValue = (ev.target as HTMLInputElement).value;
                                handleFieldChange(config.key, searchValue);
                                handleMemberSearch(searchValue);
                            }}
                            appearance="outline"
                        >
                            {memberSearchResults.map((member) => {
                                const fullName = member.fullName || `${member.vorname} ${member.nachname}`;
                                return (
                                    <Option
                                        key={member.personalID}
                                        value={member.personalID.toString()}
                                        text={fullName}
                                    >
                                        {fullName}
                                    </Option>
                                );
                            })}
                        </Combobox>
                    </div>
                );
            }

            // For other select types, build options
            let options: Array<{ id: string | number; label: string }> = [];

            switch (config.selectType) {
                case 'status':
                    options = statusOptions.map((item) => ({ id: item.value, label: item.text }));
                    break;
                case 'institute':
                    options = instituteOptions.map((item) => ({ id: item.id, label: item.bezeichnung }));
                    break;
                case 'projektart':
                    options = projektArtOptions.map((item) => ({ id: item.projektartID, label: item.bezeichnung }));
                    break;
            }

            // For status, institute, and projektart, match by ID; for others match by label
            let selectedOption;
            if (config.selectType === 'status' || config.selectType === 'institute' || config.selectType === 'projektart') {
                selectedOption = options.find((opt) => opt.id === value || String(opt.id) === value);
            } else {
                selectedOption = options.find((opt) => opt.label === value);
            }
            const displayValue = selectedOption ? selectedOption.label : '';

            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Combobox
                        value={displayValue}
                        onOptionSelect={(_, option) => {
                            const selected = options.find((opt) => opt.label === String(option.optionValue));
                            if (selected) {
                                handleFieldChange(config.key, String(selected.id));
                            }
                        }}
                        appearance="outline"
                    >
                        {options.map((opt) => {
                            const isSelected = String(opt.id) === String(value);
                            return (
                                <Option key={opt.id} value={opt.label} text={opt.label}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <span>{opt.label}</span>
                                        {isSelected && <Checkmark16Regular style={{ color: 'green' }} />}
                                    </div>
                                </Option>
                            );
                        })}
                    </Combobox>
                </div>
            );
        }

        if (config.isLargeTextarea) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Textarea
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        className={styles.largeTextarea}
                        resize="vertical"
                        textarea={{
                            style: {
                                minHeight: '120px',
                                maxHeight: 'none',
                            },
                        }}
                    />
                </div>
            );
        }

        if (config.isTextarea) {
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{config.label}</div>
                    <Textarea
                        value={value}
                        onChange={(_ev, data) => {
                            handleFieldChange(config.key, data.value);
                        }}
                        className={styles.inputField}
                    />
                </div>
            );
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{config.label}</div>
                <Input
                    value={value}
                    onChange={(_ev, data) => {
                        handleFieldChange(config.key, data.value);
                    }}
                    className={styles.inputField}
                />
            </div>
        );
    };

    const renderField = (fieldKey: string, config: Omit<FieldConfig, 'permissions' | 'key'>) => {
        // Look up permissions from allFieldPermissions array
        const fieldPermConfig = allFieldPermissions.find((f) => f.key === fieldKey);
        const fieldPermissions = fieldPermConfig?.permissions;

        // Check if field is editable for this user
        const isFieldEditable = fieldPermissions ?
            canEditField(fieldPermissions, selectedRole, project, configUserInfo) :
            false;

        // Check if field is visible for this user (read permission)
        const isFieldVisible = fieldPermissions ?
            canViewField(fieldPermissions, selectedRole, project, configUserInfo) :
            true; // Default to visible if no permissions specified

        // If field is not visible, don't render it
        if (!isFieldVisible) {
            return null;
        }

        // Add key back to config for internal use
        const fullConfig: FieldConfig = { ...config, key: fieldKey, permissions: fieldPermissions };

        // Render in edit mode if user can edit and is editing; otherwise display mode.
        // Required-but-unset fields are framed in both modes.
        const content = isFieldEditable && isEditing
            ? renderEditField(fullConfig)
            : renderDisplayField(fullConfig);

        return (
            <RequiredFieldFrame isMissing={isRequiredFieldMissing(missingFields, fieldKey)}>
                {content}
            </RequiredFieldFrame>
        );
    };

    if (!project) return null;

    return (
        <div className={styles.container}>
            {isSaving && (
                <MessageBar>
                    <MessageBarBody>
                        <MessageBarTitle>Saving...</MessageBarTitle>
                        Please wait while your changes are being saved.
                    </MessageBarBody>
                </MessageBar>
            )}
            {showSuccessMessage && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Success</MessageBarTitle>
                        Project information saved successfully!
                    </MessageBarBody>
                </MessageBar>
            )}
            {errorMessage && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {errorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
            {/* Header with Edit Button */}
            {canEditAny && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Project Information</div>
                    <div>
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    appearance="primary"
                                    icon={<Save24Regular />}
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    appearance="secondary"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button
                                appearance="outline"
                                icon={<Edit24Regular />}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Project Title Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Project Title</div>
                <div className={styles.fieldsContainer}>
                    {renderField('title', { label: 'Project Title', value: editedValues.title, editable: true })}
                    {renderField('projekttitelLangform', { label: 'Long Form', value: editedValues.projekttitelLangform, editable: true, isLargeTextarea: true })}
                    {renderField('projektKurzbeschreibung', { label: 'Project Short Description', value: editedValues.projektKurzbeschreibung, isLargeTextarea: true, editable: true })}
                </div>
            </div>

            {/* Project Data Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Project Data</div>
                <div className={styles.fieldsContainer}>
                    {renderField('projektNutzen', { label: 'Project Benefit', value: editedValues.projektNutzen, editable: true, isLargeTextarea: true })}
                    {renderField('projektSonstigeAnmerkungen', { label: 'Additional Notes', value: editedValues.projektSonstigeAnmerkungen, editable: true, isLargeTextarea: true })}
                    {renderField('anmerkungenProjektverlauf', { label: 'Project Progress Notes', value: editedValues.anmerkungenProjektverlauf, editable: true, isLargeTextarea: true })}
                    {renderField('projektWebsiteUrl', { label: 'Project Website', value: editedValues.projektWebsiteUrl, editable: true })}
                    {/* External Links Display or Edit */}
                    {canEditAny && isEditing && (allFieldPermissions.find((f) => f.key === 'externalLinks')?.permissions && canEditField(allFieldPermissions.find((f) => f.key === 'externalLinks')!.permissions, selectedRole, project, configUserInfo)) ? (
                        <div className={styles.fieldBox}>
                            <div className={styles.fieldLabel}>Links to External Websites</div>
                            <div style={{ width: '50%' }}>
                                {editedExternalLinks.length > 0 && (
                                    <div style={{ marginBottom: tokens.spacingVerticalM }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                                                    <th style={{ textAlign: 'center', padding: tokens.spacingVerticalS, fontWeight: 600, width: '60px' }}>Action</th>
                                                    <th style={{ textAlign: 'left', padding: tokens.spacingVerticalS, fontWeight: 600 }}>Title</th>
                                                    <th style={{ textAlign: 'left', padding: tokens.spacingVerticalS, fontWeight: 600 }}>URL</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editedExternalLinks.map((link, index) => (
                                                    <tr key={index} style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
                                                        <td style={{ padding: tokens.spacingVerticalS, textAlign: 'center' }}>
                                                            <Button
                                                                icon={<Delete24Regular />}
                                                                appearance="subtle"
                                                                onClick={() => handleRemoveExternalLink(index)}
                                                                title="Remove link"
                                                                style={{ color: tokens.colorStatusDangerForeground1 }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: tokens.spacingVerticalS }}>
                                                            <Input
                                                                value={link.title}
                                                                onChange={(_ev, data) => handleExternalLinkChange(index, 'title', data.value)}
                                                                placeholder="Link title"
                                                                style={{ width: '100%' }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: tokens.spacingVerticalS }}>
                                                            <Input
                                                                value={link.url}
                                                                onChange={(_ev, data) => handleExternalLinkChange(index, 'url', data.value)}
                                                                placeholder="https://example.com"
                                                                style={{ width: '100%' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <Button
                                    appearance="primary"
                                    onClick={handleAddExternalLink}
                                    style={{ marginTop: tokens.spacingVerticalM }}
                                >
                                    + Add Link
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {project?.externalLinks && project.externalLinks.length > 0 ? (
                                <div className={styles.fieldBox}>
                                    <div className={styles.fieldLabel}>Links to External Websites</div>
                                    <ul className={styles.externalLinksList}>
                                        {project.externalLinks.map((link: ExternalLinkItem, index: number) => (
                                            <li key={index} className={styles.externalLinksListItem}>
                                                <a
                                                    className={styles.externalLinksListItemLink}
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={link.url}
                                                >
                                                    <Open24Regular />
                                                    <span>{link.title || link.url}</span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div className={styles.fieldBox}>
                                    <div className={styles.fieldLabel}>Links to External Websites</div>
                                    <div className={`${styles.fieldValue} ${styles.emptyValue}`}>No external links</div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Project Timeline Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Project Timeline</div>
                <div className={styles.fieldsContainer}>
                    {renderField('projektStatusAsText', { label: 'Project Status', value: editedValues.projektStatusAsText, isSelect: true, selectType: 'status', editable: true })}
                    {renderField('operativerBeginnPlan', { label: 'Operational Start (Target)', value: editedValues.operativerBeginnPlan, isDate: true, editable: true })}
                    {renderField('operativerBeginnIst', { label: 'Operational Start (Actual)', value: editedValues.operativerBeginnIst, isDate: true, editable: true })}
                    {renderField('operativesEndePlan', { label: 'Operational End (Target)', value: editedValues.operativesEndePlan, isDate: true, editable: true })}
                    {renderField('operativesEndeIst', { label: 'Operational End (Actual)', value: editedValues.operativesEndeIst, isDate: true, editable: true })}
                    {renderField('kaufmaennischesEnde', { label: 'Commercial End', value: editedValues.kaufmaennischesEnde, isDate: true, editable: true })}
                    {renderField('datumEinreichungAngebot', { label: 'Submission/Offer Date', value: editedValues.datumEinreichungAngebot, isDate: true, editable: true })}
                    {renderField('datumAbsageAngebot', { label: 'Rejection Date', value: editedValues.datumAbsageAngebot, isDate: true, editable: true })}
                    {renderField('datumZuschlagAngebot', { label: 'Award Date', value: editedValues.datumZuschlagAngebot, isDate: true, editable: true })}
                </div>
            </div>

            {/* Release/Approval Dates Section */}
            <div className={styles.categorySection}>
                <div className={styles.categoryHeader}>Release/Approval Dates</div>
                <div className={styles.fieldsContainer}>
                    {renderField('created', { label: 'Creation Date Project Idea', value: editedValues.created, isDate: true, editable: false })}
                </div>
            </div>

            {/* Footer Bar with Save Button */}
            {canEditAny && isEditing && (
                <div className={styles.footerBar}>
                    <Button
                        appearance="primary"
                        icon={<Save24Regular />}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        Save
                    </Button>
                    <Button
                        appearance="secondary"
                        icon={<Dismiss24Regular />}
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

export default EckdatenAllgemein;
